import { useContext, useMemo, useState } from "react";
import { useRecoilValue } from "recoil";
import {
    DataGrid,
    Column,
    Paging,
    Editing,
    Pager,
    Scrolling,
} from "devextreme-react/data-grid";
import { Button as DxButton } from "devextreme-react/button";
import { userAssignmentsState } from "../../../App";

import TaskAlt from "@mui/icons-material/TaskAlt";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Button as MuiButton, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import { OidcIdentityContext } from "@rsmus/react-auth/dist/OidcIdentity";
import {
    FormatDate,
    GetUserRolloverDTO,
} from "../../../api-client-nswag/taxportal-client";

import { rollOverClientApi } from "../../../api-client-nswag/taxportal-client-runtime";
import { ArchivedFileRestoreRequest, RestoreArchiveFilesResponse } from "../../../api-client-nswag/rollover-client";


import { RolloverResponse } from "../../../pages/NewRollovers/Rollovers.types";
import { RestoreArchivedFilesModal } from "../../components/RestoreArchivedFilesModal/RestoreArchivedFilesModal"
import { RolloverInfoModal } from "../RolloverInfoModal/RolloverInfoModal";

import {
    PAGE_CAPTIONS,
    ROLLOVER_STATUSES,
} from "../../../pages/NewRollovers/Rollovers.constants";
import { dataGridConfig } from "../../../constants";
import {
    becomeUpperCase,
    convertFormatDate,
    convertUTCTimeToLocal,
    toCapitalCase,
} from "../../../util/Utils";
import { useSnackbar } from "../../../hooks";

import "./NewGridRollover.scss";
import { REACT_APP_SYSTEM_ADMIN_USERS } from "../../../envVariables";
import { SITE_ROLLOVER_COMPLETION, SITE_ROLLOVER_COMPLETION_DISPLAY } from "../../../pages/NewRollovers/NewRollovers.constants";

const {
    GRID_TITLE,
    USER,
    TYPE,
    TAX_YEAR,
    NEW_TAX_YEAR,
    ROLOVER_REQUESTED_DATE,
    ROLOVER_SCHEDULED_DATE,
    ROLLOVER_DATE,
    STATUS,
    STATUS_DETAILS,
    EXECUTION_DETAILS,
    ROLLOVER_NOW,
    FILES,
    RETRY,
    CANCEL_SCHEDULED,
    CANCELED_BY,
    CANCELED_DATE,
    NOTIFICATION_SENT,
    FIRST_NOTIFICATION_SENT,
    SECOND_NOTIFICATION_SENT,
    RESTORE_ARCHIVED_FILES,
} = PAGE_CAPTIONS;

const { SUCCESS, ERROR, PARTIALLY_SUCCESS } = ROLLOVER_STATUSES;

interface GridProps {
    data: RolloverResponse;
    appName?: string;
    handleOpenModal: (e: any) => void;
    handleOpenStatusDetailsModal: (e: any) => void;
    refreshFunction: () => Promise<void>;
    requestRollover: (selected?: string, processTrackId?: number) => void;
    retryRollover: (rollOverType: string, rolloverTrackId: number) => void;
    cancelRollover: (rolloverId: number) => void;
    requestRolloverNow: (rolloverData: GetUserRolloverDTO) => void;
}

export default function NewGridRollover({
    data,
    handleOpenModal,
    handleOpenStatusDetailsModal,
    refreshFunction,
    requestRollover,
    retryRollover,
    cancelRollover,
    requestRolloverNow,
    appName,
}: GridProps) {
    const { showSnackbar } = useSnackbar();
    // Menu state for the per-row three-dots menu
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
    const [menuRowId, setMenuRowId] = useState<number | null>(null);
    const [selectedRow, setSelectedRow] = useState<any>(null);
    const { user } = useContext(OidcIdentityContext);

    // Confirmation dialog state for Rollover Now
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingRolloverRow, setPendingRolloverRow] = useState<any>(null);

    // Restore archived files modal state
    const [isRestoreModalOpen, setRestoreModalOpen] = useState(false);
    const [archivedFiles, setArchivedFiles] = useState<any[]>([]);
    const [isLoadingArchivedFiles, setIsLoadingArchivedFiles] = useState(false);
    // Context for restore operation to avoid relying on menuRowId after menu is closed
    const [restoreContext, setRestoreContext] = useState<{ rolloverTrackId: number; taxYear?: number } | null>(null);

    // Rollover info modal state
    const [isRolloverInfoOpen, setIsRolloverInfoOpen] = useState(false);
    const [rolloverInfoRolloverId, setRolloverInfoRolloverId] = useState<number | undefined>(undefined);
    const [rolloverInfoRows, setRolloverInfoRows] = useState<any[]>([]);
    const [isLoadingRolloverInfo, setIsLoadingRolloverInfo] = useState(false);

    const userAssignments = useRecoilValue(userAssignmentsState);
    const hqSiteId = Number(sessionStorage.getItem("siteId"));

    const hasL2SupportAccess = useMemo(() => {
        if (!hqSiteId) return false;

        return userAssignments.some((a) => {
            if (Number(a.hqSiteId) !== hqSiteId) return false;
            const normalized = (a.roleName ?? "")
                .toLowerCase()
                .replace(/[\s_-]/g, "");
            return normalized === "l2support" || normalized === "csl2support";
        });
    }, [userAssignments]);

    const hasContentAndSiteAdminAccess = useMemo(() => {
        if (!hqSiteId) return false;
        const restorationAllowedRoles = new Set([
            "projectlead",
            "functionallead",
            "poweruserdelegated",
            "l2support",
            "csprojectlead",
            "csfunctionallead",
            "cspoweruserdelegated",
            "csl2support",
        ]);

        return userAssignments.some((a) => {
            if (Number(a.hqSiteId) !== hqSiteId) return false;

            const normalized = (a.roleName ?? "")
                .toLowerCase()
                .replace(/[\s_-]/g, "");

            return restorationAllowedRoles.has(normalized);
        });
    }, [hqSiteId, userAssignments]);

    const systemAdminUsers = useMemo<string[]>(() => {
    const raw = REACT_APP_SYSTEM_ADMIN_USERS;
        if (!raw) return [];
        return raw
            .split(",")
            .map((email: string) => email.replace(/"/g, "").trim().toLowerCase())
            .filter(Boolean);
    }, []);

    const isSystemAdmin = systemAdminUsers.includes(
       (user?.profile?.preferred_username ?? "").toLowerCase()
    );

    const cellRenderForDate = (e: string) => {
        return <>{convertFormatDate(e, FormatDate.DD)}</>;
    };

    const cellRenderByFieldType = (date: string, status: string) => {
        const dataField =
            status === SUCCESS || status === ERROR || status === PARTIALLY_SUCCESS
                ? date
                : "";
        return dataField ? (
            <>{convertFormatDate(dataField, FormatDate.DD)}</>
        ) : null;
    };

    const cellUpperCase = (e: string) => e && <>{becomeUpperCase(e)}</>;
    const cellCapitalCase = (e: string) => <>{toCapitalCase(e)}</>;
    const renderCheckIcon = (
        notification_one: boolean,
        notification_two: boolean,
    ) => {
        return (
            <div className="hq-icon-button">
                {notification_one && <TaskAlt titleAccess={FIRST_NOTIFICATION_SENT} />}
                {notification_two && <TaskAlt titleAccess={SECOND_NOTIFICATION_SENT} />}
            </div>
        );
    };

    const renderCancelButton = (
        value: boolean,
        id: number,
        data: GetUserRolloverDTO,
    ) => {
        return (
            value && (
                <DxButton
                    onClick={() => cancelRollover(id)}
                    icon="clear"
                    className="hq-icon-button"
                />
            )
        );
    };

    const renderRetryButton = (value: boolean, data: GetUserRolloverDTO) => {
        const { rollOverType, id } = data;
        return (
            value && (
                <DxButton
                    onClick={() => retryRollover(rollOverType, id)}
                    icon="refresh"
                    className="hq-icon-button"
                />
            )
        );
    };

    // Function to format status text for better readability
    const formatStatusText = (status: string): string => {
        if (!status) return "";

        const upperStatus = status.toString().toUpperCase();

        // Handle specific cases that need formatting
        switch (upperStatus) {
            case "PARTIALLYSUCCESS":
                return "Partially Success";
            case "INPROGRESS":
                return "In Progress";
            case "NOTSTARTED":
                return "Not Started";
            default:
                return toCapitalCase(status);
        }
    };

    // Status tooltip messages mapping
    const STATUS_TOOLTIP_MESSAGES: Record<string, string> = {
        SCHEDULED:
            "Rollover Request has been added to the queue. To modify or cancel the request, contact service.now@rsmus.com.",
        INPROGRESS: "Rollover Request is currently being processed.",
        SUCCESS: "Rollover Request completed successfully.",
        PARTIALLYSUCCESS:
            "Rollover Request has been partially completed. Please contact service.now@rsmus.com.",
        ERROR:
            "Rollover Request failed during processing. Please contact service.now@rsmus.com.",
        CANCELLED: "Rollover Request has been cancelled.",
        CANCELED: "Rollover Request has been cancelled.",
        NOTSTARTED: "Rollover Request has not started yet.",
    };

    // New: render status as a pill with styles depending on status
    const renderStatusPill = (status: string) => {
        if (!status) return null;

        const formattedText = formatStatusText(status);
        const s = (status || "").toString().toUpperCase();

        let cls = "status-pill";

        // explicit mapping for all expected values
        if (s === "SUCCESS") cls += " success";
        else if (s === "ERROR" || s === "FAIL") cls += " error";
        else if (s === "PARTIALLYSUCCESS") cls += " partial";
        else if (s === "CANCELED" || s === "CANCELLED") cls += " canceled";
        else if (s === "INPROGRESS") cls += " inprogress";
        else if (s === "SCHEDULED") cls += " scheduled";
        else if (s === "NOTSTARTED") cls += " notstarted";
        else cls += " default";

        const tooltipMessage = STATUS_TOOLTIP_MESSAGES[s] || "";

        return (
            <Tooltip title={tooltipMessage} arrow placement="top">
                <span className={cls}>{formattedText}</span>
            </Tooltip>
        );
    };

    // Open the per-row menu. stopPropagation prevents the grid row from handling the click.
    const handleOpenRowMenu = (ev: any, rowData: any) => {
        ev?.stopPropagation();
        setMenuAnchorEl(ev.currentTarget as HTMLElement);
        setMenuRowId(rowData.id);
        setSelectedRow(rowData);
    };

    const closeMenu = () => {
        setMenuAnchorEl(null);
        setMenuRowId(null);
    };

    const getRowDataById = (id: number | null) => {
        if (!id || !data?.rollovers) return undefined;
        return data.rollovers.find((r: any) => r.id === id);
    };

    const handleExecutionDetailsClick = () => {
        const row = getRowDataById(menuRowId);
        if (!row) {
            closeMenu();
            return;
        }
        // handlers in parent expect an event with row.data.id, so call with same shape
        handleOpenStatusDetailsModal({ row: { data: row } });
        closeMenu();
    };

    const handleNotificationsClick = () => {
        const row = getRowDataById(menuRowId);
        if (!row) {
            closeMenu();
            return;
        }
        // re-using handleOpenModal (parent expects event shape)
        handleOpenModal({ row: { data: row } });
        closeMenu();
    };

    const handleRolloverNowTriggerClick = () => {
        const row = getRowDataById(menuRowId);
        if (!row) {
            closeMenu();
            return;
        }
        setPendingRolloverRow(row);
        closeMenu();
        setConfirmOpen(true);
    };

    const handleConfirmRolloverNow = () => {
        if (pendingRolloverRow && requestRolloverNow) {
            pendingRolloverRow.rollOverStatus = "INPROGRESS";
            requestRolloverNow(pendingRolloverRow);
        }
        setConfirmOpen(false);
        setPendingRolloverRow(null);
    };

    const handleCancelRolloverNow = () => {
        setConfirmOpen(false);
        setPendingRolloverRow(null);
    };

    const handleRolloverNowClick = () => {
        const row = getRowDataById(menuRowId);
        if (!row) {
            closeMenu();
            return;
        }
        requestRollover(row.rollOverType, row.id);
        closeMenu();
    };

    const handleRolloverInfoClick = async () => {
        const row = getRowDataById(menuRowId);
        closeMenu();
        if (!row) return;

        const initiatedBy: string = row.createdBy ?? row.lastModifiedBy ?? "";
        const taxYear: number = Number(row.taxYear);

        setRolloverInfoRolloverId(row.id);
        setRolloverInfoRows([]);
        setIsLoadingRolloverInfo(true);
        setIsRolloverInfoOpen(true);

        try {
            const result = await rollOverClientApi.getRolloverInfo({
                hqSiteId,
                taxYear,
                initiatedBy,
            });
            const mapped = result.map((item) => ({
                id: item.rolloverRetrivalId,
                date: item.initiated,
                initiatedBy: item.initiatedBy,
                taxYear: item.taxYear,
            }));
            setRolloverInfoRows(mapped);
        } catch (error) {
            console.error("Error fetching rollover info:", error);
            setRolloverInfoRows([]);
        } finally {
            setIsLoadingRolloverInfo(false);
        }
    };

    const handleRestoreArchivedFilesClick = async () => {
        const row = getRowDataById(menuRowId);
        if (!row) {
            closeMenu();
            return;
        }

        const rolloverTrackId = row.id;

        if (!hqSiteId || !rolloverTrackId) {
            console.error("Missing hqSiteId or rolloverTrackId for fetching archived files.");
            closeMenu();
            return;
        }

        setIsLoadingArchivedFiles(true);
        closeMenu();

        try {
            const response = await rollOverClientApi.getArchivedFiles(hqSiteId, rolloverTrackId);

            // Map API response to the format expected by RestoreArchivedFilesModal
            const mappedFiles = response.map((file: ArchivedFileRestoreRequest, index: number) => ({
                id: file.fileId ?? index + 1,
                rolloverTrackDocumentId: file.rolloversTrackDocumentDetailId ?? 0,
                fileName: file.fileName ?? "",
                folder: file.folderName ?? "",
                size: file.fileSizeInBytes ?? 0, // Use numeric bytes for calculations
                formattedSize: file.formattedFileSize ?? "", // Keep formatted string for display
                zip: file.zipName ?? "",
                dmsMetadataFileId: file.dmsMetadataFileId ?? "",
                restoreStatus: file.restoreFileMessage ?? "",
            }));

            setArchivedFiles(mappedFiles);
            setRestoreModalOpen(true);

            // Store context for restore operation (rollover id and tax year)
            setRestoreContext({ rolloverTrackId: rolloverTrackId, taxYear: Number(row.taxYear) });
        } catch (error) {
            console.error("Error fetching archived files:", error);
            setArchivedFiles([]);
        } finally {
            setIsLoadingArchivedFiles(false);
        }
    };

    const handleCloseRestoreModal = () => {
        setRestoreModalOpen(false);
        setArchivedFiles([]);
        setRestoreContext(null);
    };

    const handleRestoreFiles = async (selectedFiles: any[]) => {
        console.log("handleRestoreFiles invoked. selectedFiles:", selectedFiles, "restoreContext:", restoreContext);

        if (!restoreContext) {
            console.error("No restore context available when attempting to restore files.");
            return;
        }

        const rolloverTrackId = restoreContext.rolloverTrackId;
        const taxYear = Number(restoreContext.taxYear);

        if (!rolloverTrackId || isNaN(taxYear)) {
            console.error("Missing rolloverTrackId or taxYear for restoring files.");
            return;
        }

        const payload = {
            rolloverTrackId: rolloverTrackId,
            taxYear: taxYear,
            SelectedFiles: selectedFiles.map((x) => ({
                fileId: Number(x.id),
                fileName: x.fileName,
                zipName: x.zip,
                rolloverTrackDocumentId: Number(x.rolloverTrackDocumentId),
                dMSMetadataFileId: x.dmsMetadataFileId ?? x.dMSMetadataFileId ?? "",
                folderName: x.folder,
                formattedFileSize: x.formattedSize ?? "",
                restoreStatus: x.restoreStatus ?? "",
            })),
            siteId: hqSiteId,
            userName: user?.profile.preferred_username || null,
        };

        try {
            const response: RestoreArchiveFilesResponse = await rollOverClientApi.restoreArchiveFiles(payload);
            if (response && response.isSuccess) {
                showSnackbar({
                    message: response.statusMessage || "Files restoration in progress.",
                    severity: "success",
                });
            } else {
                showSnackbar({
                    message: (response && response.statusMessage) || "Failed to restore files.",
                    severity: "error",
                });
            }
            // Refresh the archive files list in the modal
            await handleRestoreArchivedFilesRefresh(rolloverTrackId);
        } catch (error) {
            console.error("Error restoring archived files:", error);
            showSnackbar({
                message: "An error occurred while restoring files.",
                severity: "error",
            });
        }
        // keep restoreContext until modal closed
    };

    const handleRestoreArchivedFilesRefresh = async (rolloverTrackId: number) => {
        if (!hqSiteId) return;

        try {
            const response = await rollOverClientApi.getArchivedFiles(hqSiteId, rolloverTrackId);
            const mappedFiles = response.map((file: ArchivedFileRestoreRequest, index: number) => ({
                id: file.fileId ?? index + 1,
                fileName: file.fileName ?? "",
                folder: file.folderName ?? "",
                size: file.fileSizeInBytes ?? 0,
                formattedSize: file.formattedFileSize ?? "",
                zip: file.zipName ?? "",
                dmsMetadataFileId: file.dmsMetadataFileId ?? "",
                restoreStatus: file.restoreFileMessage ?? "",
                rolloverTrackDocumentId: file.rolloversTrackDocumentDetailId ?? 0,
            }));
            setArchivedFiles(mappedFiles);
        } catch (error) {
            console.error("Error refreshing archived files:", error);
        }
    };

    const canShowRestoreOptions =
        selectedRow && selectedRow.rollOverType?.toLowerCase() !== SITE_ROLLOVER_COMPLETION.toLowerCase() &&
        (
            selectedRow.rollOverStatus === "SUCCESS" ||
            (selectedRow.rollOverStatus === "PARTIALLYSUCCESS" &&
                selectedRow.isFilesUploadedToSharePoint)
        );

    return (
        <div className="hq-page__grid app-container">
            {/* Title with cyan underline */}
            <h2 className="hq-title hq-new-rollover-title">{GRID_TITLE}</h2>

            {/* Toolbar row with refresh button - positioned between title and grid */}
            <div className="hq-grid-toolbar">
                <div className="hq-grid-toolbar__actions">
                    <DxButton
                        icon="refresh"
                        onClick={refreshFunction}
                        className="hq-toolbar-button hq-refresh-button"
                        hint="Refresh"
                    />
                </div>
            </div>

            {/* Responsive grid container for horizontal scroll */}
            <div>
                <DataGrid
                    {...dataGridConfig}
                    allowColumnResizing={true}
                    columnResizingMode="widget"
                    columnAutoWidth={true}
                    height={400}
                    showBorders={true}
                    showRowLines={true}
                    showColumnLines={false}
                    dataSource={data.rollovers}
                    wordWrapEnabled={true}
                    noDataText={data.pending ? "Loading..." : "No data"}
                >
                    <Scrolling
                        mode="standard"
                        columnRenderingMode="virtual"
                        showScrollbar="always"
                    />
                    <Paging enabled={true} defaultPageSize={10} />
                    <Pager visible={true} displayMode="full" />
                    <Editing mode="row" allowUpdating useIcons />
                    <Column
                        alignment="left"
                        dataField="lastModifiedBy"
                        caption={USER}
                        cellRender={(e) =>
                            e.data?.lastModifiedBy !== null
                                ? e.data?.lastModifiedBy?.toLowerCase()
                                : e.data?.createdBy?.toLowerCase()
                        }
                    />
                    <Column
                        alignment="left"
                        dataField="rollOverType"
                        caption={TYPE}
                        cellRender={(e) =>
                            e.data.rollOverType?.toLowerCase() === SITE_ROLLOVER_COMPLETION.toLowerCase()
                                ? <>{SITE_ROLLOVER_COMPLETION_DISPLAY}</>
                                : cellCapitalCase(e.data.rollOverType)
                        }
                    />
                    <Column
                        alignment="left"
                        dataField="taxYear"
                        caption={TAX_YEAR}
                        cellRender={(e) => e.data.taxYear}
                        allowEditing={false}
                    />
                    <Column
                        alignment="left"
                        dataField="newTaxYear"
                        caption={NEW_TAX_YEAR}
                        cellRender={(e) => e.data.newTaxYear}
                        allowEditing={false}
                    />
                    <Column
                        dataField="rolloverRequestedDate"
                        caption={ROLOVER_REQUESTED_DATE}
                        cellRender={(e) => (
                            <>
                                {convertFormatDate(e.data.rolloverRequestedDate, "MM/DD/YYYY")}
                            </>
                        )}
                        allowEditing={false}
                    />
                    <Column
                        dataField="scheduledDate"
                        caption={ROLOVER_SCHEDULED_DATE}
                        cellRender={(e) => (
                            <>{convertFormatDate(e.data.scheduledDate, "MM/DD/YYYY")}</>
                        )}
                        allowEditing={false}
                    />
                    <Column
                        dataField="lastModified"
                        caption="Rollover Date"
                        width={100}
                        cellRender={(e) => {
                            // Only show date if status is one of these, else show nothing
                            const { lastModified, rollOverStatus } = e.data;
                            const showDate =
                                rollOverStatus === "SUCCESS" ||
                                rollOverStatus === "ERROR" ||
                                rollOverStatus === "PARTIALLYSUCCESS";

                            return showDate && lastModified ? (
                                <>{convertUTCTimeToLocal(lastModified, "MM/DD/YYYY")}</>
                            ) : null;
                        }}
                        allowEditing={false}
                    />
                    <Column
                        dataField="rollOverStatus"
                        caption={STATUS}
                        width={120}
                        cellRender={(e) => renderStatusPill(e.data.rollOverStatus)}
                        allowEditing={false}
                    />
                    <Column
                        type="buttons"
                        caption={RETRY}
                        cellRender={(e) =>
                            renderRetryButton(
                                e.data.retryRollOver &&
                                !e.data.isRolloverArchiveFolderNotFound,
                                e.data,
                            )
                        }
                    />
                    <Column
                        type="buttons"
                        caption={CANCEL_SCHEDULED}
                        cellRender={(e) =>
                            renderCancelButton(
                                e.data.cancelRollOver && e.data.rollOverStatus !== "INPROGRESS",
                                e.data.id,
                                e.data,
                            )
                        }
                    />
                    <Column
                        alignment="left"
                        dataField="canceledBy"
                        caption={CANCELED_BY}
                        cellRender={(e) =>
                            e.data.canceledBy !== null
                                ? e.data.canceledBy.toLowerCase()
                                : e.data.canceledBy
                        }
                        allowEditing={false}
                    />
                    <Column
                        dataField="canceledDate"
                        caption={CANCELED_DATE}
                        cellRender={(e) => (
                            <>{convertFormatDate(e.data.canceledDate, "MM/DD/YYYY")}</>
                        )}
                        allowEditing={false}
                    />

                    {/* NEW: Actions column with three-dots menu */}
                    <Column
                        alignment="center"
                        caption={""}
                        allowEditing={false}
                        cellRender={(e) => (
                            <div className="hq-action-menu-cell">
                                <IconButton
                                    size="small"
                                    onClick={(ev) => handleOpenRowMenu(ev, e.data)}
                                    className="hq-action-button"
                                    aria-label="row actions"
                                >
                                    <MoreVertIcon fontSize="small" />
                                </IconButton>
                            </div>
                        )}
                    />
                </DataGrid>
            </div>

            <Menu
                id="row-actions-menu"
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={closeMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                disableScrollLock
            >
                <MenuItem onClick={handleExecutionDetailsClick}>
                    {EXECUTION_DETAILS}
                </MenuItem>
                {selectedRow &&
                    ["FILES"].includes(
                        selectedRow.rollOverType
                    ) && (
                        <MenuItem onClick={handleNotificationsClick}>
                            Notifications
                        </MenuItem>
                    )}
                {selectedRow && selectedRow.rollOverStatus === "SCHEDULED" && (hasL2SupportAccess || isSystemAdmin) && (
                    <MenuItem onClick={handleRolloverNowTriggerClick}>{ROLLOVER_NOW}</MenuItem>
                )}
                {canShowRestoreOptions && (hasContentAndSiteAdminAccess || isSystemAdmin) && (
                    <>
                        <MenuItem onClick={handleRestoreArchivedFilesClick}>
                            {RESTORE_ARCHIVED_FILES}
                        </MenuItem>

                        <MenuItem onClick={handleRolloverInfoClick}>
                            Restore Audit History
                        </MenuItem>
                    </>
                )}
            </Menu>

            <Dialog
                open={confirmOpen}
                onClose={handleCancelRolloverNow}
                aria-labelledby="rollover-now-confirm-title"
            >
                <DialogTitle id="rollover-now-confirm-title">Rollover Now</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        A rollover is resource intensive process. Running it outside the nightly
                        windows could impact system performance. Are you sure you want to proceed?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <MuiButton onClick={handleCancelRolloverNow}>Cancel</MuiButton>
                    <MuiButton onClick={handleConfirmRolloverNow} variant="contained" color="primary" autoFocus>
                        Proceed
                    </MuiButton>
                </DialogActions>
            </Dialog>

            <RestoreArchivedFilesModal
                open={isRestoreModalOpen}
                onClose={handleCloseRestoreModal}
                archivedFiles={archivedFiles}
                onRestore={handleRestoreFiles}
            />

            <RolloverInfoModal
                open={isRolloverInfoOpen}
                onClose={() => setIsRolloverInfoOpen(false)}
                rolloverId={rolloverInfoRolloverId}
                rows={rolloverInfoRows}
                pending={isLoadingRolloverInfo}
            />
        </div>
    );
}
